/**
 * FeaturedEventPopup — iter-66.
 *
 * Second-tier promotional popup shown 5 s after the Newsletter popup has been
 * closed *if* an event flagged `is_featured=True` is currently published in
 * the CMS (endpoint `/api/special-events/featured`).
 *
 * Behaviour :
 *   • Fetches `/api/special-events/featured` on mount. If the endpoint returns
 *     no event → the component stays dormant and never renders.
 *   • Listens for the `bbr:newsletter-closed` custom event dispatched by the
 *     NewsletterPopup on dismiss / successful submit. If the newsletter step
 *     was already completed in a previous visit (session/localStorage flag),
 *     the popup fires 5 s after mount instead — so returning visitors still
 *     see the promo.
 *   • Once dismissed (any way), stores a session flag so it's not shown twice
 *     during the same visit. If a *different* event becomes featured, the
 *     flag key incorporates the event id → new event = fresh popup.
 *   • Fully responsive, premium design (backdrop-blur, hero image, elegant
 *     typography, single primary CTA).
 */
import { useEffect, useState } from "react";
import { X, ArrowRight, CalendarDays } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const NL_SESSION_KEY = "bbr_newsletter_popup_seen";
const NL_PERMANENT_KEY = "bbr_newsletter_subscribed";
const DELAY_AFTER_CLOSE_MS = 5000;

function eventDismissKey(id) { return `bbr_featured_event_seen__${id}`; }

function formatFrenchDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

function firstEventDate(ev) {
  if (!ev) return "";
  if (ev.event_kind === "multi_day" && ev.start_date) {
    return ev.start_date === ev.end_date
      ? formatFrenchDate(ev.start_date)
      : `${formatFrenchDate(ev.start_date)} — ${formatFrenchDate(ev.end_date)}`;
  }
  const first = Array.isArray(ev.event_dates) && ev.event_dates.length > 0 ? ev.event_dates[0] : null;
  return first ? formatFrenchDate(first) : "";
}

export default function FeaturedEventPopup() {
  const [event, setEvent] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Fetch the currently featured event on mount.
  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND}/api/special-events/featured`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        // Endpoint returns { event: {...} | null } — unwrap.
        const ev = data.event || null;
        if (!ev || !ev.id) return;
        // Skip if already dismissed this session for THIS event.
        try {
          if (window.sessionStorage.getItem(eventDismissKey(ev.id))) return;
        } catch { /* ignore */ }
        setEvent(ev);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Schedule the popup once the newsletter step is over.
  useEffect(() => {
    if (!event) return undefined;
    let timer = null;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setOpen(true), DELAY_AFTER_CLOSE_MS);
    };
    // If the newsletter was already seen/submitted in a previous session,
    // fire immediately (5 s from mount).
    let alreadyClosed = false;
    try {
      alreadyClosed = !!(window.sessionStorage.getItem(NL_SESSION_KEY) || window.localStorage.getItem(NL_PERMANENT_KEY));
    } catch { /* ignore */ }
    if (alreadyClosed) schedule();
    const onClosed = () => schedule();
    window.addEventListener("bbr:newsletter-closed", onClosed);
    return () => {
      window.removeEventListener("bbr:newsletter-closed", onClosed);
      clearTimeout(timer);
    };
  }, [event]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function close() {
    setOpen(false);
    if (event?.id) {
      try { window.sessionStorage.setItem(eventDismissKey(event.id), "1"); } catch { /* ignore */ }
    }
  }

  function book() {
    // Route to the event landing page. `/event/:eventId` (singular) is the
    // established public event-detail route (see App.js).
    close();
    if (event?.id) navigate(`/event/${event.id}`);
    else navigate("/univers/evenementiel");
  }

  if (!open || !event) return null;

  const date = firstEventDate(event);
  const image = event.image_url || "";

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 sm:p-6 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={`Événement exclusif : ${event.title || ""}`}
      onClick={close}
      data-testid="featured-event-popup"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <div
        className="relative w-full max-w-md bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)] animate-modal-in overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Fermer"
          className="absolute top-3 right-3 z-10 p-1.5 bg-white/85 hover:bg-white text-[#0A0A0A]/70 hover:text-[#0A0A0A] transition-colors"
          data-testid="featured-event-close"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        {/* Hero image */}
        {image && (
          <div className="relative w-full aspect-[16/10] bg-[#0A0A0A]/5 overflow-hidden">
            <img
              src={image}
              alt={event.title || "Événement exclusif"}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute top-3 left-3 text-[0.55rem] tracking-[0.4em] uppercase text-white bg-[#B8922A]/95 px-3 py-1">
              Événement exclusif
            </div>
          </div>
        )}

        <div className="p-6 sm:p-8">
          {!image && (
            <div className="text-[0.55rem] tracking-[0.4em] uppercase text-[#B8922A] mb-3">
              · Événement exclusif ·
            </div>
          )}

          <h3 className="font-serif font-light text-2xl sm:text-3xl leading-[1.1] text-[#0A0A0A] mb-3" data-testid="featured-event-title">
            {event.title || "Un rendez-vous exceptionnel"}
          </h3>

          {date && (
            <div className="inline-flex items-center gap-2 text-[0.68rem] tracking-[0.25em] uppercase text-[#0A0A0A]/60 mb-4" data-testid="featured-event-date">
              <CalendarDays size={13} strokeWidth={1.5} />
              {date}
            </div>
          )}

          {(event.subtitle || event.description) && (
            <p className="text-sm md:text-base text-[#0A0A0A]/70 leading-relaxed font-light mb-6 line-clamp-4">
              {event.subtitle || event.description}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={book}
              className="group inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#B8922A] text-white px-6 py-3.5 text-[0.68rem] tracking-[0.32em] uppercase transition-colors"
              data-testid="featured-event-cta"
            >
              {event.cta_label || "Réserver"}
              <ArrowRight size={14} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              type="button"
              onClick={close}
              className="inline-flex items-center justify-center gap-2 text-[0.62rem] tracking-[0.32em] uppercase text-[#0A0A0A]/55 hover:text-[#0A0A0A] pt-1"
              data-testid="featured-event-later"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
